import React, { useRef, useEffect, useState } from 'react';
import { motion } from 'framer-motion';

interface VideoBackgroundProps {
  videoSrc: string;
  fallbackComponent?: React.ReactNode;
  overlay?: boolean;
  className?: string;
}

const VideoBackground: React.FC<VideoBackgroundProps> = ({
  videoSrc,
  fallbackComponent,
  overlay = true,
  className = ""
}) => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);

  useEffect(() => {
    const video = videoRef.current;
    if (!video) return;

    // Delay video loading to improve initial page load
    const timer = setTimeout(() => {
      const handleCanPlay = () => {
        setIsLoaded(true);
      };
      const handleError = (e: any) => {
        console.error('Video error:', e);
        setHasError(true);
      };

      video.addEventListener('canplay', handleCanPlay);
      video.addEventListener('error', handleError);

      return () => {
        video.removeEventListener('canplay', handleCanPlay);
        video.removeEventListener('error', handleError);
      };
    }, 100); // Small delay to prioritize other content

    return () => clearTimeout(timer);
  }, [videoSrc]);

  if (hasError && fallbackComponent) {
    return <>{fallbackComponent}</>;
  }

  return (
    <div className={`fixed inset-0 z-0 ${className}`}>
      <motion.video
        ref={videoRef}
        autoPlay
        muted
        loop
        playsInline
        preload="metadata"
        className="w-full h-full object-cover"
        initial={{ opacity: 0 }}
        animate={{ opacity: isLoaded ? 1 : 0 }}
        transition={{ duration: 1 }}
        onError={() => setHasError(true)}
      >
        <source src={videoSrc} type="video/mp4" />
      </motion.video>
      
      {overlay && (
        <div className="absolute inset-0 bg-black/70" />
      )}
      
      {!isLoaded && !hasError && fallbackComponent && (
        <div className="absolute inset-0">
          {fallbackComponent}
        </div>
      )}
    </div>
  );
};

export default VideoBackground;